import type { Dimensions } from './types';

interface BoundingBox {
  page: number;
  xmin: number;
  ymin: number;
  width: number;
  height: number;
}

interface PageInfo {
  outer: Dimensions;
  inner: Dimensions;
  scale: number;
  element: HTMLElement;
}

interface Coordinates {
  x: number;
  y: number;
  width: number;
  height: number;
}

async function createHighlights(
  boundingBoxes: BoundingBox[],
  pageDimensions?: Dimensions,
  highlightColor?: string,
): Promise<void> {
  for (const box of boundingBoxes) {
    try {
      await createHighlightWithRetry(box, pageDimensions, highlightColor);
    } catch (error) {
      console.error(`Failed to create highlight for page ${box.page}:`, error);
    }
  }
}

async function createHighlightWithRetry(
  boundingBox: BoundingBox,
  pageDimensions?: Dimensions,
  highlightColor?: string,
  maxRetries: number = 10,
): Promise<HTMLElement> {
  let attempts = 0;

  while (attempts < maxRetries) {
    try {
      return createHighlight(boundingBox, pageDimensions, highlightColor);
    } catch (error) {
      attempts++;

      if (attempts >= maxRetries) {
        throw new Error(
          `Failed to create highlight after ${maxRetries} attempts: ${error}`,
        );
      }

      // We will keep retrying until a max of around 12 seconds after first attempts
      const delay = Math.min(100 * Math.pow(2, attempts - 1), 2000);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw new Error('Unexpected error in retry logic');
}

function createHighlight(
  boundingBox: BoundingBox,
  pageDimensions?: Dimensions,
  highlightColor?: string,
): HTMLElement {
  const containerData = getPageInfo(boundingBox.page);
  const htmlCoord = convertCoordinates(
    boundingBox,
    containerData,
    pageDimensions,
  );

  const defaultHighlightColor = 'rgba(204, 247, 255, 0.40)';
  highlightColor = highlightColor || defaultHighlightColor;

  // Create the highlighting element
  const highlight = document.createElement('div');
  highlight.className = 'pdf-highlight';
  highlight.style.cssText = `
    position: absolute;
    left: ${htmlCoord.x - 8}px;
    top: ${htmlCoord.y - 8}px;
    width: ${htmlCoord.width + 16}px;
    height: ${htmlCoord.height + 16}px;
    background-color: ${highlightColor};
    border-radius: 4px;
    z-index: 2;
  `;

  // Place the highlighting element
  containerData.element.appendChild(highlight);

  return highlight;
}

function clearHighlights(): void {
  document.querySelectorAll('.pdf-highlight').forEach((element) => {
    element.remove();
  });
}

function scrollToHighlight(
  boundingBox: BoundingBox,
  pageDimensions?: Dimensions,
): void {
  try {
    const pageContainer = document.getElementById(
      `outer_page_${boundingBox.page}`,
    );

    if (!pageContainer) {
      throw new Error(`Page container not found for page ${boundingBox.page}`);
    }

    // Since 4gen loads in page content as you scroll, and the highlight exists within this
    // inner page content, we first scroll to the approximate location of the highlight.
    const outerHeight =
      parseInt(pageContainer.style.height) ||
      pageContainer.getBoundingClientRect().height;

    if (pageDimensions && outerHeight > 0) {
      const scaleY = outerHeight / pageDimensions.height;
      const approximateY = boundingBox.ymin * scaleY;

      // Get absolute position of the highlight in the document
      const pageRect = pageContainer.getBoundingClientRect();
      const absolutePageY = window.scrollY + pageRect.top;
      const absoluteHighlightY = absolutePageY + approximateY;

      const viewportHeight = window.innerHeight;
      const targetScrollY = absoluteHighlightY - viewportHeight / 2;

      window.scrollTo({
        top: Math.max(0, targetScrollY),
        behavior: 'auto',
      });
    } else {
      // Fall back to page-level scrolling
      pageContainer.scrollIntoView({
        behavior: 'auto',
        block: 'center',
      });
    }
  } catch (error) {
    console.error('Error scrolling to highlight:', error);
  }
}

function findJpedalWithinOuterPage(
  outerContainer: HTMLElement,
): HTMLElement | null {
  const inLightDom = outerContainer.querySelector('#jpedal');
  if (inLightDom) {
    return inLightDom as HTMLElement;
  }

  // If page is inside of a shadow DOM, we need to walk the DOM to find the jpedal node.
  const walker = document.createTreeWalker(
    outerContainer,
    NodeFilter.SHOW_ELEMENT,
    null,
  );
  let node = walker.nextNode() as HTMLElement | null;
  while (node) {
    const { shadowRoot } = node;
    if (shadowRoot) {
      const inShadow = shadowRoot.querySelector('#jpedal');
      if (inShadow) {
        return inShadow as HTMLElement;
      }
    }
    node = walker.nextNode() as HTMLElement | null;
  }

  return null;
}

function getPageInfo(pageNumber: number): PageInfo {
  const outerContainer = document.getElementById(`outer_page_${pageNumber}`);
  const innerContainer = outerContainer
    ? findJpedalWithinOuterPage(outerContainer)
    : null;

  if (!outerContainer || !innerContainer) {
    throw new Error(`Page containers not found for page ${pageNumber}`);
  }

  // Extract dimensions
  const outerWidth =
    parseInt(outerContainer.style.width) ||
    outerContainer.getBoundingClientRect().width;
  const outerHeight =
    parseInt(outerContainer.style.height) ||
    outerContainer.getBoundingClientRect().height;
  const innerWidth = parseInt(innerContainer.style.width);
  const innerHeight = parseInt(innerContainer.style.height);

  // Extract CSS transform scale using getBoundingClientRect for accuracy
  let scale = 1;
  if (innerContainer.offsetWidth > 0) {
    scale =
      innerContainer.getBoundingClientRect().width / innerContainer.offsetWidth;
  }

  return {
    outer: { width: outerWidth, height: outerHeight },
    inner: { width: innerWidth, height: innerHeight },
    scale: scale,
    element: innerContainer,
  };
}

function convertCoordinates(
  boundingBox: BoundingBox,
  containerData: PageInfo,
  pageDimensions?: Dimensions,
): Coordinates {
  const dimensions = pageDimensions || { width: 612, height: 792 };

  // Map stored logical page size into the rendered BuildVu page dimensions
  const scaleX = containerData.inner.width / dimensions.width;
  const scaleY = containerData.inner.height / dimensions.height;

  const htmlX = boundingBox.xmin * scaleX;
  const htmlY = boundingBox.ymin * scaleY;
  const htmlWidth = boundingBox.width * scaleX;
  const htmlHeight = boundingBox.height * scaleY;

  return { x: htmlX, y: htmlY, width: htmlWidth, height: htmlHeight };
}

export { createHighlights, clearHighlights, scrollToHighlight };
